import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { Mail, Instagram, Youtube, Music2, Loader2, CheckCircle2 } from "lucide-react";
import { submitContactMessage } from "@/lib/contact.functions";

export const Route = createFileRoute("/contact")({
  head: () => ({
    meta: [
      { title: "Contact - Smartasaurus" },
      { name: "description", content: "About Smartasaurus, our mission, and how to reach us. Pitches, partnerships, press, media, and newsletter support - all in one place." },
      { property: "og:title", content: "Contact Smartasaurus" },
      { property: "og:description", content: "Pitches, partnerships, press, and curious questions." },
      { property: "og:url", content: "https://smartasaurus.com/contact" },
      { name: "twitter:card", content: "summary" },
      { name: "twitter:title", content: "Contact Smartasaurus" },
      { name: "twitter:description", content: "Pitches, partnerships, press, and curious questions." },
    ],
    links: [{ rel: "canonical", href: "https://smartasaurus.com/contact" }],
  }),
  component: ContactPage,
});

type Topic = "general" | "feedback" | "partnerships" | "press" | "media" | "newsletter";

function ContactPage() {
  const submit = useServerFn(submitContactMessage);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [topic, setTopic] = useState<Topic>("general");
  const [message, setMessage] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "ok" | "error">("idle");
  const [error, setError] = useState<string | null>(null);

  // Read ?topic=feedback after hydration so SSR and client agree on initial render.
  useEffect(() => {
    const t = new URLSearchParams(window.location.search).get("topic");
    const allowed: Topic[] = ["general", "feedback", "partnerships", "press", "media", "newsletter"];
    if (allowed.includes((t ?? "") as Topic)) setTopic(t as Topic);
  }, []);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setStatus("loading");
    setError(null);
    try {
      await submit({ data: { name, email, topic, message } });
      setStatus("ok");
      setName(""); setEmail(""); setTopic("general"); setMessage("");
    } catch (err) {
      setStatus("error");
      setError(err instanceof Error ? err.message : "Something went wrong. Please email us directly.");
    }
  }

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main id="main-content" className="mx-auto max-w-5xl px-4 py-20 md:px-8 md:py-28">
        <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-primary">· Contact</div>
        <h1 className="mt-3 font-display text-5xl uppercase leading-[0.95] md:text-7xl">
          Say <span className="text-gradient-curiosity">hello</span>.
        </h1>

        <div className="mt-10 grid gap-10 md:grid-cols-[1.1fr_1fr]">
          <section>
            <h2 className="font-display text-2xl uppercase">About Smartasaurus</h2>
            <p className="mt-3 text-muted-foreground">
              Smartasaurus is a discovery platform for the genuinely curious. We publish daily, fact-checked stories about weird animals, ocean mysteries, dangerous nature, space, the human body, and unexplained phenomena.
            </p>
            <h3 className="mt-8 font-display text-xl uppercase">Our mission</h3>
            <p className="mt-2 text-muted-foreground">
              Make the world feel bigger, stranger, and more wonderful - one discovery at a time.
            </p>

            <h3 className="mt-8 font-display text-xl uppercase">Direct email</h3>
            <div className="mt-3">
              <a href="mailto:support@smartasaurus.com" className="group block rounded-2xl border border-border bg-card p-5 transition hover:border-primary">
                <Mail className="h-5 w-5 text-primary" />
                <div className="mt-3 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Email us</div>
                <div className="mt-1 break-all text-sm">support@smartasaurus.com</div>
              </a>
            </div>

            <h3 className="mt-8 font-display text-xl uppercase">Social</h3>
            <div className="mt-3 flex flex-wrap items-center gap-3">
              {[
                { Icon: Instagram, label: "Instagram", href: "https://www.instagram.com/smartasaurusofficial" },
                { Icon: Music2, label: "TikTok", href: "https://www.tiktok.com/@smartasaurusofficial" },
                { Icon: Youtube, label: "Facebook", href: "https://www.facebook.com/share/1DRXUg7iRN/" },
              ].map(({ Icon, label, href }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-full border border-border px-3 py-2 text-xs text-muted-foreground transition hover:border-primary hover:text-foreground"
                >
                  <Icon className="h-4 w-4" /> {label}
                </a>
              ))}
            </div>

          </section>

          <section className="rounded-2xl border border-border bg-card p-6 md:p-8">
            <h2 className="font-display text-2xl uppercase">Send a message</h2>
            <p className="mt-2 text-sm text-muted-foreground">We read everything. Most replies within a week.</p>

            {status === "ok" ? (
              <div className="mt-6 flex items-start gap-3 rounded-xl border border-primary/30 bg-primary/10 p-4">
                <CheckCircle2 className="mt-0.5 h-5 w-5 text-primary" />
                <div>
                  <div className="font-semibold">Message sent.</div>
                  <p className="text-sm text-muted-foreground">Thanks - we'll get back to you soon.</p>
                </div>
              </div>
            ) : (
              <form onSubmit={onSubmit} className="mt-6 space-y-4">
                <div>
                  <label htmlFor="contact-name" className="block text-[11px] font-bold uppercase tracking-widest text-muted-foreground">Name</label>
                  <input id="contact-name" name="name" autoComplete="name" style={{fontSize:"16px"}}
                    required minLength={1} maxLength={120}
                    value={name} onChange={(e) => setName(e.target.value)}
                    className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-base outline-none focus:border-primary"
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="block text-[11px] font-bold uppercase tracking-widest text-muted-foreground">Email</label>
                  <input id="contact-email" name="email" autoComplete="email" style={{fontSize:"16px"}}
                    type="email" required maxLength={254}
                    value={email} onChange={(e) => setEmail(e.target.value)}
                    className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-base outline-none focus:border-primary"
                  />
                </div>
                <div>
                  <label htmlFor="contact-topic" className="block text-[11px] font-bold uppercase tracking-widest text-muted-foreground">Topic</label>
                  <select id="contact-topic" name="topic" style={{fontSize:"16px"}}
                    value={topic} onChange={(e) => setTopic(e.target.value as Topic)}
                    className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-base outline-none focus:border-primary"
                  >
                    <option value="general">General</option>
                    <option value="feedback">Feedback / what you want more of</option>
                    <option value="partnerships">Partnerships</option>
                    <option value="press">Press</option>
                    <option value="media">Media Requests</option>
                    <option value="newsletter">Newsletter Support</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="contact-message" className="block text-[11px] font-bold uppercase tracking-widest text-muted-foreground">Message</label>
                  <textarea id="contact-message" name="message" style={{fontSize:"16px"}}
                    required minLength={10} maxLength={4000} rows={6}
                    value={message} onChange={(e) => setMessage(e.target.value)}
                    placeholder={topic === "feedback" ? "What's working for you? What's missing? More articles on a topic, more quizzes, a category we should add - tell us what would keep you coming back." : undefined}
                    className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-base outline-none focus:border-primary"
                  />
                </div>
                {error && <div className="text-xs text-destructive">{error}</div>}
                <button
                  type="submit"
                  disabled={status === "loading"}
                  className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-xs font-bold uppercase tracking-wider text-primary-foreground transition hover:brightness-110 disabled:opacity-60"
                >
                  {status === "loading" && <Loader2 className="h-4 w-4 animate-spin" />}
                  Send message
                </button>
              </form>
            )}
          </section>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}
